(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.POExpressCheckoutTracking = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const STORAGE_KEY = 'checkout_tealium_values';
  const EXPRESS_SOURCE = 'cart_express';
  const EXPRESS_SELECTOR = [
    '.shopify-payment-button',
    '[data-shopify="dynamic-checkout-cart"]',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    'shopify-buy-it-now-button',
    'shopify-apple-pay-button',
    'shopify-google-pay-button',
    'shopify-paypal-button',
    'shop-pay-wallet-button'
  ].join(',');
  const EXPRESS_CONTAINER_SELECTOR = '.additional-checkout-buttons';
  const INTERACTIVE_CHECKOUT_SELECTOR = [
    'button',
    'a',
    'input[type="submit"]',
    '[role="button"]'
  ].join(',');
  const STANDARD_CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'button[name="checkout_shortcut"]',
    '[name="checkout"][type="submit"]'
  ].join(',');
  const EXPRESS_STATE_KEYS = [
    'paymentType',
    'paymentTypeSource',
    'paymentTypeUpdatedAt',
    'checkoutEventPending',
    'checkoutEventPendingAt',
    'checkoutStartedPayload',
    'checkoutStartedPayloadAt'
  ];
  const CHECKOUT_BASE_PAYLOAD_KEYS = [
    'Page_Language',
    'Page_DeviceType',
    'Page_Country',
    'Order_Currency',
    'User_LoginStatus',
    'User_Email_MD5',
    'User_Email_SHA256',
    'User_LoginType',
    'User_Id',
    'Order_DiscountCode',
    'Order_DiscountName',
    'Order_DiscountAmount',
    'Order_DiscountAmountNet'
  ];
  const PAYMENT_TYPE_BY_TAG_NAME = {
    'shopify-apple-pay-button': 'Apple Pay',
    'shopify-google-pay-button': 'Google Pay',
    'shopify-paypal-button': 'PayPal',
    'shop-pay-wallet-button': 'Shop Pay',
    'shopify-shop-pay-button': 'Shop Pay'
  };

  function readStorage(storage) {
    if (!storage || typeof storage.getItem !== 'function') {
      return {};
    }

    try {
      const parsedValue = JSON.parse(storage.getItem(STORAGE_KEY) || '{}');
      return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
        ? parsedValue
        : {};
    } catch (_error) {
      return {};
    }
  }

  function writeStorage(storage, value) {
    if (!storage || typeof storage.setItem !== 'function') {
      return false;
    }

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function updateStorage(storage, update) {
    const currentValue = readStorage(storage);
    const nextValue = update(currentValue) || currentValue;
    writeStorage(storage, nextValue);
    return nextValue;
  }

  function isPromiseLike(value) {
    return Boolean(
      value &&
      (typeof value === 'object' || typeof value === 'function') &&
      typeof value.then === 'function'
    );
  }

  function clearExpressState(storage) {
    return updateStorage(storage, (checkoutTealiumValues) => {
      EXPRESS_STATE_KEYS.forEach((key) => delete checkoutTealiumValues[key]);
      return checkoutTealiumValues;
    });
  }

  function clearPendingState(storage) {
    return updateStorage(storage, (checkoutTealiumValues) => {
      delete checkoutTealiumValues.checkoutEventPending;
      delete checkoutTealiumValues.checkoutEventPendingAt;
      return checkoutTealiumValues;
    });
  }

  function isElementLike(value) {
    return Boolean(
      value &&
      value.nodeType === 1 &&
      typeof value.matches === 'function'
    );
  }

  function getTrackingElements(event) {
    if (typeof event?.composedPath === 'function') {
      return event.composedPath().filter(isElementLike);
    }

    const elements = [];
    let currentElement = event?.target;
    while (isElementLike(currentElement)) {
      elements.push(currentElement);
      currentElement = currentElement.parentElement;
    }
    return elements;
  }

  function matchesAny(elements, selector) {
    return elements.some((element) => {
      try {
        return element.matches(selector);
      } catch (_error) {
        return false;
      }
    });
  }

  function getPaymentTypeFromTagName(elements) {
    for (const element of elements) {
      const tagName = String(element.tagName || '').toLowerCase();
      if (PAYMENT_TYPE_BY_TAG_NAME[tagName]) {
        return PAYMENT_TYPE_BY_TAG_NAME[tagName];
      }
    }
    return '';
  }

  function inferPaymentType(elements) {
    const paymentTypeFromTagName = getPaymentTypeFromTagName(elements);
    if (paymentTypeFromTagName) {
      return paymentTypeFromTagName;
    }

    const fingerprint = elements.map((element) => {
      const getAttribute = typeof element.getAttribute === 'function'
        ? (name) => element.getAttribute(name)
        : () => '';
      return [
        element.tagName,
        getAttribute('id'),
        typeof element.className === 'string' ? element.className : getAttribute('class'),
        getAttribute('name'),
        getAttribute('aria-label'),
        getAttribute('title'),
        getAttribute('data-testid'),
        getAttribute('data-shopify'),
        getAttribute('part'),
        element.textContent
      ].filter(Boolean).join(' ');
    }).join(' ').toLowerCase();

    if (/apple[\s_-]*pay/.test(fingerprint)) return 'Apple Pay';
    if (/google[\s_-]*pay|gpay/.test(fingerprint)) return 'Google Pay';
    if (/shop(?:ify)?[\s_-]*pay/.test(fingerprint)) return 'Shop Pay';
    if (/pay[\s_-]*pal/.test(fingerprint)) return 'PayPal';
    if (/klarna/.test(fingerprint)) return 'Klarna';
    if (/affirm/.test(fingerprint)) return 'Affirm';
    return '';
  }

  function isExpressCheckoutClick(elements) {
    if (getPaymentTypeFromTagName(elements) || matchesAny(elements, EXPRESS_SELECTOR)) {
      return true;
    }

    return matchesAny(elements, EXPRESS_CONTAINER_SELECTOR)
      && matchesAny(elements, INTERACTIVE_CHECKOUT_SELECTOR);
  }

  function isStandardCheckoutClick(elements) {
    return matchesAny(elements, STANDARD_CHECKOUT_SELECTOR);
  }

  function createCheckoutStartedPayload({
    basePayload = {},
    products,
    paymentType = '',
    cartId = '',
    discountCodes = []
  } = {}) {
    const checkoutBasePayload = {};
    CHECKOUT_BASE_PAYLOAD_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(basePayload, key)) {
        checkoutBasePayload[key] = basePayload[key];
      }
    });

    const payload = {
      ...checkoutBasePayload,
      id: 'VirtualPage-View',
      Page_Type: 'Payment',
      Page_Section1: 'Checkout',
      Page_Section2: 'Standard',
      Page_Section3: ''
    };

    if (products && typeof products === 'object') {
      payload.Products = products;
    }
    if (cartId) {
      payload.Order_CartId = cartId;
    }
    if (paymentType) {
      payload.Order_PaymentType = paymentType;
    } else {
      delete payload.Order_PaymentType;
    }
    if (Array.isArray(discountCodes) && discountCodes.length > 0) {
      payload.Order_DiscountCode = discountCodes.join(', ');
    }

    return payload;
  }

  function createExpressCheckoutTracker({
    storage,
    now = Date.now,
    buildCheckoutStartedPayload,
    sendEvent
  } = {}) {
    let registeredTarget = null;

    async function handleClick(event) {
      const elements = getTrackingElements(event);
      if (!elements.length) {
        return { handled: false };
      }

      if (isStandardCheckoutClick(elements)) {
        clearExpressState(storage);
        return { handled: true, express: false };
      }

      if (!isExpressCheckoutClick(elements)) {
        return { handled: false };
      }

      const paymentType = inferPaymentType(elements);
      const timestamp = Number(now());

      updateStorage(storage, (checkoutTealiumValues) => {
        checkoutTealiumValues.paymentTypeSource = EXPRESS_SOURCE;
        checkoutTealiumValues.checkoutEventPending = true;
        checkoutTealiumValues.checkoutEventPendingAt = timestamp;
        delete checkoutTealiumValues.checkoutStartedPayload;
        delete checkoutTealiumValues.checkoutStartedPayloadAt;

        if (paymentType) {
          checkoutTealiumValues.paymentType = paymentType;
          checkoutTealiumValues.paymentTypeUpdatedAt = timestamp;
        } else {
          delete checkoutTealiumValues.paymentType;
          delete checkoutTealiumValues.paymentTypeUpdatedAt;
        }

        return checkoutTealiumValues;
      });

      if (typeof buildCheckoutStartedPayload !== 'function') {
        return { handled: true, express: true, paymentType, sent: false };
      }

      try {
        let payload = buildCheckoutStartedPayload({ event, elements, paymentType });
        if (isPromiseLike(payload)) {
          payload = await payload;
        }
        if (!payload || typeof payload !== 'object') {
          return { handled: true, express: true, paymentType, sent: false };
        }

        if (paymentType && !payload.Order_PaymentType) {
          payload = { ...payload, Order_PaymentType: paymentType };
        }

        updateStorage(storage, (checkoutTealiumValues) => {
          checkoutTealiumValues.checkoutStartedPayload = payload;
          checkoutTealiumValues.checkoutStartedPayloadAt = timestamp;
          return checkoutTealiumValues;
        });

        let sent = false;
        if (typeof sendEvent === 'function') {
          const sendResult = sendEvent(payload);
          sent = isPromiseLike(sendResult)
            ? await sendResult !== false
            : sendResult !== false;
        }

        if (sent) {
          clearPendingState(storage);
        }

        return { handled: true, express: true, paymentType, sent, payload };
      } catch (error) {
        return { handled: true, express: true, paymentType, sent: false, error };
      }
    }

    function register(target) {
      if (!target || typeof target.addEventListener !== 'function' || registeredTarget) {
        return false;
      }

      registeredTarget = target;
      target.addEventListener('click', (event) => {
        void handleClick(event);
      }, true);
      return true;
    }

    return {
      handleClick,
      register
    };
  }

  return {
    STORAGE_KEY,
    clearExpressState,
    createCheckoutStartedPayload,
    createExpressCheckoutTracker,
    getTrackingElements,
    inferPaymentType,
    isExpressCheckoutClick,
    isStandardCheckoutClick
  };
});
